import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconBuilding,
  IconChevronDown,
  IconSearch,
  Input,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  Skeleton,
} from '../../../ui/v2';
import type { PropertySummary, StoreGroupOption } from '../api';
import { HOTEL_BASE, hotelPaths } from '../model';

/**
 * Chooses the current property (components doc §3). Mirrors `StoreSwitcher`
 * so the two shells' scope selectors sit at the same height and read as the
 * same kind of control. Choosing keeps the section (Overview / Stays /
 * Settings), drops the query string, and never reloads.
 *
 * For a platform admin the same menu also changes the store group (`groups`
 * with `onPickGroup`): there is one scope control, not a separate page.
 */

export type HotelSection = 'overview' | 'stays' | 'integrations' | 'settings';

const PROPERTY_PATH = new RegExp(
  `^${HOTEL_BASE}/properties/([^/?#]+)(?:/([^/?#]+))?`,
);

/**
 * The `:propertyId` segment of a `/hotel/properties/:propertyId/*` pathname,
 * decoded as the router decodes it for `useParams`; null when there is none.
 * Not an id: `05` or `abc` names no property, and the page says Not found.
 */
export function hotelPropertyParamOf(pathname: string): string | null {
  const segment = PROPERTY_PATH.exec(pathname)?.[1];
  if (segment === undefined) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** pathname → section. Stay detail and Guest count as Stays. */
export function hotelSectionOf(pathname: string): HotelSection {
  const tail = PROPERTY_PATH.exec(pathname)?.[2];
  if (tail === 'stays') return 'stays';
  if (tail === 'integrations') return 'integrations';
  if (tail === 'settings') return 'settings';
  return 'overview';
}

/** (section, property) → the section's path at that property. */
export function hotelSectionPath(
  section: HotelSection,
  propertyId: number,
): string {
  if (section === 'stays') return hotelPaths.stays(propertyId);
  if (section === 'integrations') return hotelPaths.integrations(propertyId);
  if (section === 'settings') return hotelPaths.settings(propertyId);
  return hotelPaths.overview(propertyId);
}

/** Search appears from this many properties, as `ScopeSwitcher` does for venues. */
const SEARCH_FROM = 6;

const describe = (p: PropertySummary) =>
  `${p.time_zone} · ${p.currency} · Voice ${p.voice_booking_enabled ? 'on' : 'off'}`;

const tile =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-v2-sm bg-v2-accent/20 text-v2-accent';
const box =
  'flex h-12 w-full items-center gap-2.5 rounded-v2 border border-v2-line bg-v2-surface/60 px-2.5 text-left';

export interface PropertySwitcherProps {
  /** undefined → loading skeleton. */
  properties: PropertySummary[] | undefined;
  current: PropertySummary | null;
  /** Where the current URL sits, so the section is kept. */
  section: HotelSection;
  /** Closes the drawer after a navigation. */
  onNavigate?: () => void;
  /**
   * Platform admin only: the store groups to offer in the same menu, and
   * what a choice does. `groups` undefined while they load.
   */
  groups?: StoreGroupOption[];
  groupsFailed?: boolean;
  currentGroupId?: number | null;
  onPickGroup?: (id: number) => void;
}

export const PropertySwitcher: React.FC<PropertySwitcherProps> = ({
  properties,
  current,
  section,
  onNavigate,
  groups,
  groupsFailed = false,
  currentGroupId = null,
  onPickGroup,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchor = useRef<HTMLButtonElement>(null);

  if (properties === undefined) {
    return (
      <div className="v2-inverse px-3">
        <div aria-busy="true" className={box}>
          <span className="sr-only">Loading properties…</span>
          <span className={tile}>
            <IconBuilding size={14} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3" width="60%" />
            <Skeleton className="h-2.5" width="40%" />
          </span>
        </div>
      </div>
    );
  }

  const picksGroup = onPickGroup !== undefined;
  const canSwitch = properties.length > 1 || picksGroup;
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? properties.filter((p) => p.name.toLowerCase().includes(needle))
    : properties;
  const filteredGroups = needle
    ? groups?.filter((g) => g.name.toLowerCase().includes(needle))
    : groups;

  const close = () => {
    setOpen(false);
    setSearch('');
  };
  const choose = (id: number) => {
    close();
    if (id === current?.id) return;
    onNavigate?.();
    navigate(hotelSectionPath(section, id));
  };
  const chooseGroup = (id: number) => {
    close();
    if (id === currentGroupId) return;
    onPickGroup?.(id);
  };

  return (
    <div className="v2-inverse relative px-3">
      <button
        ref={anchor}
        type="button"
        disabled={!canSwitch}
        aria-haspopup={canSwitch ? 'menu' : undefined}
        aria-expanded={canSwitch ? open : undefined}
        aria-label={current ? `Property: ${current.name}` : 'Select property'}
        onClick={() => setOpen((o) => !o)}
        className={clsx(box, canSwitch && 'hover:bg-v2-sunken')}
      >
        <span className={tile}>
          <IconBuilding size={14} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-v2-13 font-semibold leading-4 text-v2-fg">
            {current?.name ?? 'Select property'}
          </span>
          <span className="v2-num block truncate text-v2-11 leading-4 text-v2-fg-3">
            {current
              ? `${current.time_zone} · ${current.currency}`
              : properties.length === 0 && picksGroup
                ? currentGroupId === null
                  ? 'Choose a group…'
                  : 'No hotels in this group'
                : `${properties.length} properties`}
          </span>
        </span>
        {canSwitch && <IconChevronDown className="text-v2-fg-3" />}
      </button>
      <Menu
        open={open}
        onClose={close}
        anchorRef={anchor}
        width={256}
        className="left-3"
      >
        {(properties.length >= SEARCH_FROM ||
          (groups?.length ?? 0) >= SEARCH_FROM) && (
          <div className="p-1 pb-2">
            <Input
              size="sm"
              autoFocus
              aria-label="Find a property"
              placeholder="Find a property…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leading={<IconSearch size={14} />}
            />
          </div>
        )}
        {properties.length > 0 && (
          <>
            <MenuLabel>Property</MenuLabel>
            <div className="v2-scroll max-h-[280px] overflow-y-auto">
              {filtered.map((p) => (
                <MenuItem
                  key={p.id}
                  selected={p.id === current?.id}
                  description={describe(p)}
                  onClick={() => choose(p.id)}
                >
                  {p.name}
                </MenuItem>
              ))}
              {filtered.length === 0 && (
                <p className="px-2 py-3 text-v2-12 text-v2-fg-3">
                  No properties match.
                </p>
              )}
            </div>
          </>
        )}
        {picksGroup && (
          <>
            {properties.length > 0 && <MenuSeparator />}
            <MenuLabel>Store group</MenuLabel>
            {groupsFailed ? (
              <p className="px-2 py-3 text-v2-12 text-v2-fg-3">
                Store groups couldn’t load.
              </p>
            ) : filteredGroups === undefined ? (
              <div aria-busy="true" className="px-2 py-2">
                <span className="sr-only">Loading store groups…</span>
                <Skeleton className="h-3" width="70%" />
              </div>
            ) : (
              <div className="v2-scroll max-h-[224px] overflow-y-auto">
                {filteredGroups.map((g) => (
                  <MenuItem
                    key={g.id}
                    selected={g.id === currentGroupId}
                    onClick={() => chooseGroup(g.id)}
                  >
                    {g.name}
                  </MenuItem>
                ))}
                {filteredGroups.length === 0 && (
                  <p className="px-2 py-3 text-v2-12 text-v2-fg-3">
                    No groups match.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </Menu>
    </div>
  );
};
